import React, { useState, useEffect } from 'react';
import { Rocket, Loader2, CheckCircle2, AlertTriangle, Layers } from 'lucide-react';
import { schoolApi } from '../../api/school';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';

export const OpenNewYearWizard = ({ open, onOpenChange, orgId, years = [], onDone }) => {
  const activeYear = years.find((y) => y.is_active) || years[0] || null;
  const [form, setForm] = useState({
    name: '', start_date: '', end_date: '',
    source_academic_year_id: '', clone_structure: true,
    create_fiscal_year: true, fiscal_year_name: '',
    set_active: true,
  });
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (open) {
      setResult(null);
      setForm((f) => ({ ...f, source_academic_year_id: activeYear?.id || '' }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.name.trim()) { toast.error('Please enter the new year name (e.g. 2025-2026)'); return; }
    if (form.clone_structure && !form.source_academic_year_id) { toast.error('Pick a source year to clone from'); return; }
    setSubmitting(true);
    try {
      const payload = {
        organization_id: orgId,
        name: form.name.trim(),
        start_date: form.start_date || null,
        end_date: form.end_date || null,
        source_academic_year_id: form.clone_structure ? form.source_academic_year_id : null,
        clone_structure: form.clone_structure,
        set_active: form.set_active,
        create_fiscal_year: form.create_fiscal_year,
        fiscal_year_name: form.fiscal_year_name.trim() || form.name.trim(),
        fiscal_year_start: form.start_date || null,
        fiscal_year_end: form.end_date || null,
      };
      const res = await schoolApi.openNewYear(payload);
      setResult(res);
      toast.success('New academic year opened');
      onDone && onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to open new year');
    } finally { setSubmitting(false); }
  };

  const close = () => { onOpenChange(false); };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="open-new-year-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Rocket className="w-5 h-5 text-primary" /> Open New Academic Year</DialogTitle>
          <DialogDescription>Create a new, independent academic year. Optionally clone last year's classes, sections and subjects, and create a paired fiscal year.</DialogDescription>
        </DialogHeader>

        {result ? (
          <div className="space-y-4" data-testid="open-new-year-result">
            <div className="flex items-center gap-2 text-emerald-600 font-medium"><CheckCircle2 className="w-5 h-5" /> {result.academic_year?.name} created{result.academic_year?.is_active ? ' and activated' : ''}.</div>
            <div className="grid grid-cols-3 gap-3">
              {['classes', 'sections', 'subjects'].map((k) => (
                <div key={k} className="rounded-lg border border-border p-3 text-center">
                  <p className="text-2xl font-semibold">{result.cloned?.[k] ?? 0}</p>
                  <p className="text-xs text-muted-foreground capitalize">{k} cloned</p>
                </div>
              ))}
            </div>
            <div className="text-sm text-muted-foreground flex items-center gap-2">
              <Layers className="w-4 h-4" /> Fiscal year: {result.fiscal_year_id ? 'created' : 'not created'}
            </div>
            {(result.warnings || []).length > 0 && (
              <div className="rounded-lg border border-amber-500/40 bg-amber-500/5 p-3 text-sm text-amber-700 space-y-1">
                {result.warnings.map((w, i) => (
                  <div key={i} className="flex items-start gap-2"><AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" /> {w}</div>
                ))}
              </div>
            )}
            <p className="text-xs text-muted-foreground">Next: use <strong>Promote Students</strong> to enroll students from the previous year into the new classes.</p>
            <DialogFooter><Button onClick={close} data-testid="open-new-year-done">Done</Button></DialogFooter>
          </div>
        ) : (
          <div className="space-y-5">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="sm:col-span-3"><Label>New year name *</Label><Input value={form.name} onChange={(e) => set('name', e.target.value)} placeholder="2025-2026" data-testid="new-year-name" /></div>
              <div><Label>Start date</Label><Input type="date" value={form.start_date} onChange={(e) => set('start_date', e.target.value)} /></div>
              <div><Label>End date</Label><Input type="date" value={form.end_date} onChange={(e) => set('end_date', e.target.value)} /></div>
              <div className="flex items-end pb-1">
                <label className="flex items-center gap-2 text-sm"><Switch checked={form.set_active} onCheckedChange={(v) => set('set_active', v)} data-testid="set-active-switch" /> Set as active</label>
              </div>
            </div>

            <div className="rounded-lg border border-border p-4 space-y-3">
              <label className="flex items-center gap-2 text-sm font-medium"><Switch checked={form.clone_structure} onCheckedChange={(v) => set('clone_structure', v)} data-testid="clone-structure-switch" /> Clone academic structure from a previous year</label>
              {form.clone_structure && (
                <div>
                  <Label>Source year</Label>
                  <Select value={form.source_academic_year_id} onValueChange={(v) => set('source_academic_year_id', v)}>
                    <SelectTrigger data-testid="source-year-select"><SelectValue placeholder="Select year to clone from" /></SelectTrigger>
                    <SelectContent>
                      {years.map((y) => <SelectItem key={y.id} value={y.id}>{y.name}{y.is_active ? ' (active)' : ''}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground mt-1">Copies classes, sections, subjects and teacher assignments into the new year. Students are enrolled separately via Promote Students.</p>
                </div>
              )}
            </div>

            <div className="rounded-lg border border-border p-4 space-y-3">
              <label className="flex items-center gap-2 text-sm font-medium"><Switch checked={form.create_fiscal_year} onCheckedChange={(v) => set('create_fiscal_year', v)} data-testid="create-fy-switch" /> Create paired fiscal year (financial independence)</label>
              {form.create_fiscal_year && (
                <div>
                  <Label>Fiscal year name</Label>
                  <Input value={form.fiscal_year_name} onChange={(e) => set('fiscal_year_name', e.target.value)} placeholder={form.name || 'FY 2025-2026'} data-testid="fy-name-input" />
                  <p className="text-xs text-muted-foreground mt-1">Uses the same start/end dates. Skipped automatically if the dates overlap an existing fiscal year.</p>
                </div>
              )}
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={close} disabled={submitting}>Cancel</Button>
              <Button onClick={submit} disabled={submitting} className="gap-2" data-testid="open-new-year-submit">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Rocket className="w-4 h-4" />} Open Year
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
